import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { CheckCircle, Calendar, ExternalLink } from 'lucide-react';
import { fmtDate, PARTNERSHIP_TYPE_LABELS } from '../../lib/utils';
import { toast } from 'sonner';

const PRIORITY_DOT = { high: 'bg-magenta', medium: 'bg-amber-400', low: 'bg-slate-300' };

export function UpcomingFollowupsWidget({ days = 14 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get(`/followups/upcoming?days=${days}`).then((r) => {
      setItems(r.data);
      setLoading(false);
    });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const markComplete = async (item) => {
    const notes = prompt(`Complete follow-up with ${item.company_name}?\nEnter notes:`);
    if (notes === null) return;
    try {
      await api.post(`/leads/${item.id}/complete-followup`, {
        notes: notes || 'Follow-up completed',
        activity_type: 'followup',
      });
      toast.success('Follow-up completed');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const reschedule = async (item) => {
    const date = prompt(`Reschedule ${item.company_name} follow-up to (YYYY-MM-DD):`);
    if (!date) return;
    try {
      await api.put(`/leads/${item.id}`, {
        ...item,
        next_followup_date: new Date(date).toISOString(),
        company_name: item.company_name,
        primary_contact: item.primary_contact,
      });
      toast.success('Rescheduled');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="upcoming-followups-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-navy">Upcoming Follow-Ups</h3>
        <span className="text-[11px] text-slate-500 uppercase tracking-wider">{items.length} active</span>
      </div>
      {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>}
      {!loading && items.length === 0 && <div className="text-sm text-slate-500 py-8 text-center">No follow-ups scheduled</div>}
      <div className="space-y-2">
        {items.slice(0, 8).map((it) => (
          <div key={it.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0" data-testid={`followup-${it.id}`}>
            <div className="flex items-start justify-between gap-3 mb-1.5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[it.next_followup_priority] || PRIORITY_DOT.medium}`} />
                  <span className="font-semibold text-sm text-navy truncate">{it.company_name}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {it.primary_contact} · {PARTNERSHIP_TYPE_LABELS[it.partnership_type] || 'Partnership'}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-semibold text-magenta">{fmtDate(it.next_followup_date)}{it.next_followup_time ? ` ${it.next_followup_time}` : ''}</div>
                <div className="text-[10px] text-slate-500">{it.next_followup_type || 'Follow-Up'}</div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-slate-500">Assigned: <span className="text-navy font-medium">{it.assigned_to_name}</span></span>
              <div className="flex gap-1">
                <Link to="/leads">
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" data-testid={`open-${it.id}`}>
                    <ExternalLink className="w-3 h-3 mr-1" /> Open
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => markComplete(it)} data-testid={`complete-${it.id}`}>
                  <CheckCircle className="w-3 h-3 mr-1" /> Complete
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => reschedule(it)} data-testid={`reschedule-${it.id}`}>
                  <Calendar className="w-3 h-3 mr-1" /> Reschedule
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
